export function createStructureOps({
  CHUNK_SIZE,
  SETTLEMENT_CELL_SIZE,
  SETTLEMENT_BASE_CHANCE,
  POI_CELL_SIZE,
  POI_BASE_CHANCE,
  SHIPWRECK_CELL_SIZE,
  SHIPWRECK_BASE_CHANCE,
  SKY_FEATURE_CELL_SIZE,
  SKY_FEATURE_BASE_CHANCE,
  structureScale = 1,
  WORLD_MIN_Y,
  WATER_LEVEL,
  settlementCache,
  poiCache,
  shipwreckCache,
  skyFeatureCache,
  hash2i,
  hash3i,
  valueNoise2,
  valueNoise3,
  setBoundedCache,
  clamp,
  smooth,
  lerp,
  columnNoise,
  chooseStoneType,
  worldToChunk,
  chunkKey,
  worldToBlock,
  getDimensionAtCoord,
  getBlockIdAt,
  canUseNaturalAt,
  shapeColumnSurface,
  clearColumnAbove,
  setGeneratedBlockAt,
  clearGeneratedBlockAt,
  createNaturalBlock,
  createBlock,
  sealFluidWithBarrier,
  convertAdjacentWaterToGlass
}) {
  const structureSizeScale = Math.max(1, Number(structureScale) || 1);
  const structureRadiusScale = Math.max(1, Math.sqrt(structureSizeScale));
  const structureHeightScale = Math.max(1, 1 + ((structureSizeScale - 1) * 0.85));

  function scaleDistance(value, min = 1) {
    return Math.max(min, Math.round(Number(value) * structureRadiusScale));
  }

  function scaleHeight(value, min = 1) {
    return Math.max(min, Math.round(Number(value) * structureHeightScale));
  }

  function nearestGridDistance(v, step) {
    const nearest = Math.round(v / step) * step;
    return Math.abs(v - nearest);
  }

  function rangesOverlap(minA, maxA, minB, maxB) {
    return minA <= maxB && maxA >= minB;
  }

  function getChunkBounds(cx, cz) {
    const minX = cx * CHUNK_SIZE;
    const minZ = cz * CHUNK_SIZE;
    return {
      minX,
      maxX: minX + CHUNK_SIZE - 1,
      minZ,
      maxZ: minZ + CHUNK_SIZE - 1
    };
  }

  function squareProfileTouchesChunk(profile, bounds, extra = 0) {
    if (!profile || !bounds) return false;
    const radius = Math.max(0, Number(profile.radius) || 0) + Math.max(0, Number(extra) || 0);
    return rangesOverlap(profile.centerX - radius, profile.centerX + radius, bounds.minX, bounds.maxX)
      && rangesOverlap(profile.centerZ - radius, profile.centerZ + radius, bounds.minZ, bounds.maxZ);
  }

  function shipwreckProfileTouchesChunk(profile, bounds) {
    if (!profile || !bounds) return false;
    const halfX = profile.alongX
      ? Math.max(0, Number(profile.lengthHalf) || 0) + 2
      : Math.max(0, Number(profile.halfBeam) || 0) + 3;
    const halfZ = profile.alongX
      ? Math.max(0, Number(profile.halfBeam) || 0) + 3
      : Math.max(0, Number(profile.lengthHalf) || 0) + 2;
    return rangesOverlap(profile.centerX - halfX, profile.centerX + halfX, bounds.minX, bounds.maxX)
      && rangesOverlap(profile.centerZ - halfZ, profile.centerZ + halfZ, bounds.minZ, bounds.maxZ);
  }

  function collectProfilesForChunk(cx, cz, cellSize, resolveCellProfile, touchesChunk) {
    const bounds = getChunkBounds(cx, cz);
    const minCellX = Math.floor(bounds.minX / cellSize) - 1;
    const maxCellX = Math.floor(bounds.maxX / cellSize) + 1;
    const minCellZ = Math.floor(bounds.minZ / cellSize) - 1;
    const maxCellZ = Math.floor(bounds.maxZ / cellSize) + 1;
    const profiles = [];

    for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ++) {
      for (let cellX = minCellX; cellX <= maxCellX; cellX++) {
        const profile = resolveCellProfile(cellX, cellZ);
        if (!profile) continue;
        if (!touchesChunk(profile, bounds)) continue;
        profiles.push(profile);
      }
    }

    return profiles;
  }

  function getSquareProfileAtFromCandidates(x, z, profiles = null) {
    if (!Array.isArray(profiles) || profiles.length <= 0) return null;

    let best = null;
    let bestDist = Infinity;
    for (const profile of profiles) {
      if (!profile) continue;
      const dx = x - profile.centerX;
      const dz = z - profile.centerZ;
      const dist = Math.max(Math.abs(dx), Math.abs(dz));
      if (dist > profile.radius) continue;
      if (dist < bestDist) {
        bestDist = dist;
        best = profile;
      }
    }
    return best;
  }

  function getShipwreckAtFromCandidates(x, z, profiles = null) {
    if (!Array.isArray(profiles) || profiles.length <= 0) return null;

    let best = null;
    let bestDist = Infinity;
    for (const profile of profiles) {
      if (!profile) continue;
      const dx = x - profile.centerX;
      const dz = z - profile.centerZ;
      const along = profile.alongX ? dx : dz;
      const across = profile.alongX ? dz : dx;
      if (Math.abs(along) > profile.lengthHalf + 2 || Math.abs(across) > profile.halfBeam + 3) continue;
      const dist = Math.max(Math.abs(dx), Math.abs(dz));
      if (dist < bestDist) {
        bestDist = dist;
        best = profile;
      }
    }
    return best;
  }

  function getSkyFeatureAtFromCandidates(x, z, profiles = null) {
    if (!Array.isArray(profiles) || profiles.length <= 0) return null;

    let best = null;
    let bestDist = Infinity;
    for (const profile of profiles) {
      if (!profile) continue;
      const dx = x - profile.centerX;
      const dz = z - profile.centerZ;
      const dist = Math.hypot(dx, dz);
      if (dist > profile.radius + 2.5) continue;
      if (dist < bestDist) {
        bestDist = dist;
        best = profile;
      }
    }
    return best;
  }

  function settlementStyleConfig(style) {
    if (style === 'modern') {
      return {
        roadStep: scaleDistance(8, 4),
        roadWidth: 1,
        lotStep: scaleDistance(10, 5),
        buildRoll: 0.26,
        roadId: 25,
        fillId: 24,
        groundId: 24
      };
    }

    if (style === 'desert') {
      return {
        roadStep: scaleDistance(7, 4),
        roadWidth: 1,
        lotStep: scaleDistance(9, 5),
        buildRoll: 0.34,
        roadId: 21,
        fillId: 21,
        groundId: 21
      };
    }

    return {
      roadStep: scaleDistance(7, 4),
      roadWidth: 1,
      lotStep: scaleDistance(9, 5),
      buildRoll: 0.32,
      roadId: 5,
      fillId: 2,
      groundId: 1
    };
  }

  function getSettlementForCell(cellX, cellZ) {
    const key = `${cellX},${cellZ}`;
    if (settlementCache.has(key)) return settlementCache.get(key);

    const isSpawnCell = cellX === 0 && cellZ === 0;
    const spawnRoll = hash2i(cellX, cellZ, 1701);
    let centerX = (cellX * SETTLEMENT_CELL_SIZE) + Math.floor(SETTLEMENT_CELL_SIZE * (0.2 + hash2i(cellX, cellZ, 1711) * 0.6));
    let centerZ = (cellZ * SETTLEMENT_CELL_SIZE) + Math.floor(SETTLEMENT_CELL_SIZE * (0.2 + hash2i(cellX, cellZ, 1723) * 0.6));
    if (isSpawnCell) {
      let best = null;
      for (let sx = 8; sx <= 40; sx += 4) {
        for (let sz = 8; sz <= 40; sz += 4) {
          const info = columnNoise(sx, sz);
          const score = info.height - (info.biome === 'wetland' ? 9 : 0) - (info.biome === 'mountain' ? 4 : 0);
          if (!best || score > best.score) {
            best = { x: sx, z: sz, score };
          }
        }
      }
      if (best) {
        centerX = best.x;
        centerZ = best.z;
      }
    }
    const centerInfo = columnNoise(centerX, centerZ);
    const chanceBoost = centerInfo.biome === 'lowland' ? 0.08 : centerInfo.biome === 'forest' ? 0.04 : 0.0;
    const mountainPenalty = centerInfo.biome === 'mountain' ? 0.12 : 0;
    if (!isSpawnCell && spawnRoll > (SETTLEMENT_BASE_CHANCE + chanceBoost - mountainPenalty)) {
      setBoundedCache(settlementCache, key, null);
      return null;
    }

    if (centerInfo.height <= WATER_LEVEL + 1) {
      setBoundedCache(settlementCache, key, null);
      return null;
    }

    const styleRoll = hash2i(cellX, cellZ, 1733);
    let style = isSpawnCell ? 'modern' : 'plains';
    if (!isSpawnCell) {
      if (styleRoll < 0.33) style = 'modern';
      else if (styleRoll < 0.66) style = 'desert';
    }

    if (style === 'desert' && centerInfo.humidity > 0.58 && hash2i(cellX, cellZ, 1741) > 0.45) {
      style = 'plains';
    }
    if (style === 'modern' && centerInfo.biome === 'wetland' && hash2i(cellX, cellZ, 1747) > 0.35) {
      style = 'plains';
    }

    const styleCfg = settlementStyleConfig(style);
    const rawRadius = isSpawnCell ? 30 : style === 'modern'
      ? 24 + Math.floor(hash2i(cellX, cellZ, 1759) * 7)
      : style === 'desert'
        ? 20 + Math.floor(hash2i(cellX, cellZ, 1763) * 6)
        : 19 + Math.floor(hash2i(cellX, cellZ, 1769) * 6);
    const radius = scaleDistance(rawRadius, 12);

    const baseY = centerInfo.height + (style === 'modern' ? 1 : 0);
    const profile = {
      key,
      cellX,
      cellZ,
      centerX,
      centerZ,
      baseY,
      radius,
      style,
      ...styleCfg
    };
    setBoundedCache(settlementCache, key, profile);
    return profile;
  }

  function getSettlementAt(x, z) {
    const cellX = Math.floor(x / SETTLEMENT_CELL_SIZE);
    const cellZ = Math.floor(z / SETTLEMENT_CELL_SIZE);

    let best = null;
    let bestDist = Infinity;
    for (let cz = cellZ - 1; cz <= cellZ + 1; cz++) {
      for (let cx = cellX - 1; cx <= cellX + 1; cx++) {
        const profile = getSettlementForCell(cx, cz);
        if (!profile) continue;
        const dx = x - profile.centerX;
        const dz = z - profile.centerZ;
        const dist = Math.max(Math.abs(dx), Math.abs(dz));
        if (dist > profile.radius) continue;
        if (dist < bestDist) {
          bestDist = dist;
          best = profile;
        }
      }
    }
    return best;
  }

  function getLotSpec(profile, localX, localZ) {
    const lotGX = Math.round(localX / profile.lotStep);
    const lotGZ = Math.round(localZ / profile.lotStep);
    const cx = lotGX * profile.lotStep;
    const cz = lotGZ * profile.lotStep;

    if (Math.abs(cx) > profile.radius - 4 || Math.abs(cz) > profile.radius - 4) return null;
    if (Math.abs(cx) <= profile.roadStep + 1 || Math.abs(cz) <= profile.roadStep + 1) return null;

    const seedX = profile.cellX * 73 + lotGX;
    const seedZ = profile.cellZ * 89 + lotGZ;
    const roll = hash2i(seedX, seedZ, 1801);
    if (roll < profile.buildRoll) return null;

    const halfW = scaleDistance(2 + Math.floor(hash2i(seedX, seedZ, 1811) * 2), 2);
    const halfD = scaleDistance(2 + Math.floor(hash2i(seedX, seedZ, 1823) * 2), 2);

    let height = 4 + Math.floor(hash2i(seedX, seedZ, 1831) * 3);
    if (profile.style === 'modern') height = 6 + Math.floor(hash2i(seedX, seedZ, 1837) * 8);
    else if (profile.style === 'desert') height = 3 + Math.floor(hash2i(seedX, seedZ, 1847) * 3);
    height = scaleHeight(height, 3);

    let doorSide = 'south';
    if (Math.abs(cx) >= Math.abs(cz)) doorSide = cx > 0 ? 'west' : 'east';
    else doorSide = cz > 0 ? 'north' : 'south';

    if (profile.style === 'modern') {
      return {
        cx,
        cz,
        halfW,
        halfD,
        height,
        doorSide,
        wallId: 39,
        floorId: 25,
        roofId: 24,
        windowId: 23,
        accentId: 33
      };
    }

    if (profile.style === 'desert') {
      return {
        cx,
        cz,
        halfW,
        halfD,
        height,
        doorSide,
        wallId: 21,
        floorId: 21,
        roofId: 33,
        windowId: 23,
        accentId: 22
      };
    }

    return {
      cx,
      cz,
      halfW,
      halfD,
      height,
      doorSide,
      wallId: 6,
      floorId: 5,
      roofId: 34,
      windowId: 23,
      accentId: 32
    };
  }

  function isRoadColumn(profile, localX, localZ) {
    const coreRoad = Math.abs(localX) <= profile.roadWidth || Math.abs(localZ) <= profile.roadWidth;
    if (coreRoad) return true;

    const gridRoad = nearestGridDistance(localX, profile.roadStep) <= profile.roadWidth
      || nearestGridDistance(localZ, profile.roadStep) <= profile.roadWidth;
    if (!gridRoad) return false;

    if (profile.style === 'modern') return true;
    if (profile.style === 'desert') return Math.abs(localX) + Math.abs(localZ) < profile.radius * 1.5;
    return hash2i(localX, localZ, 1861) > 0.14;
  }

  function applySettlementRoadColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    const y = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.roadId, profile.fillId);
    clearColumnAbove(x, z, y + 1, y + 4);

    const nearX = nearestGridDistance(localX, profile.roadStep) <= profile.roadWidth;
    const nearZ = nearestGridDistance(localZ, profile.roadStep) <= profile.roadWidth;
    if (!nearX || !nearZ) return;

    const nearestX = Math.round(localX / profile.roadStep) * profile.roadStep;
    const nearestZ = Math.round(localZ / profile.roadStep) * profile.roadStep;
    if (localX !== nearestX || localZ !== nearestZ) return;
    if (Math.abs(localX) < 3 && Math.abs(localZ) < 3) return;
    if (hash2i(x, z, 1877) < 0.6) return;

    if (profile.style === 'modern') {
      setGeneratedBlockAt(x, y + 1, z, 24, ck);
      setGeneratedBlockAt(x, y + 2, z, 24, ck);
      setGeneratedBlockAt(x, y + 3, z, 23, ck);
      setGeneratedBlockAt(x, y + 4, z, 12, ck);
      return;
    }

    setGeneratedBlockAt(x, y + 1, z, 20, ck);
    setGeneratedBlockAt(x, y + 2, z, 20, ck);
    setGeneratedBlockAt(x, y + 3, z, 12, ck);
  }

  function applySettlementBuildingColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    const lot = getLotSpec(profile, localX, localZ);
    if (!lot) return false;

    const relX = localX - lot.cx;
    const relZ = localZ - lot.cz;
    if (Math.abs(relX) > lot.halfW || Math.abs(relZ) > lot.halfD) return false;

    const floorY = shapeColumnSurface(x, z, surfaceY, targetY, ck, lot.floorId, profile.fillId);
    clearColumnAbove(x, z, floorY + 1, floorY + lot.height + 4);

    const atBoundary = Math.abs(relX) === lot.halfW || Math.abs(relZ) === lot.halfD;
    const atCorner = Math.abs(relX) === lot.halfW && Math.abs(relZ) === lot.halfD;
    const doorHere = (
      (lot.doorSide === 'north' && relZ === -lot.halfD && relX === 0) ||
      (lot.doorSide === 'south' && relZ === lot.halfD && relX === 0) ||
      (lot.doorSide === 'west' && relX === -lot.halfW && relZ === 0) ||
      (lot.doorSide === 'east' && relX === lot.halfW && relZ === 0)
    );

    for (let y = 1; y <= lot.height; y++) {
      if (atBoundary) {
        if (doorHere && y === 1) {
          setGeneratedBlockAt(x, floorY + y, z, 16, ck);
          continue;
        }
        if (doorHere && y === 2) {
          clearGeneratedBlockAt(x, floorY + y, z);
          continue;
        }
        const windowBand = y >= Math.max(2, Math.floor(lot.height * 0.35)) && y <= lot.height - 1;
        const modernWindow = profile.style === 'modern' && !atCorner && windowBand && (y % 2 === 0);
        const regularWindow = profile.style !== 'modern' && !atCorner && windowBand && ((Math.abs(relX) + Math.abs(relZ) + y) % 4 === 0);
        if (modernWindow || regularWindow) setGeneratedBlockAt(x, floorY + y, z, lot.windowId, ck);
        else setGeneratedBlockAt(x, floorY + y, z, lot.wallId, ck);
      } else {
        if (profile.style === 'modern' && y % 3 === 0 && y < lot.height - 1) {
          setGeneratedBlockAt(x, floorY + y, z, lot.accentId, ck);
        } else {
          clearGeneratedBlockAt(x, floorY + y, z);
        }
      }
    }

    setGeneratedBlockAt(x, floorY + lot.height + 1, z, lot.roofId, ck);
    if (profile.style === 'modern' && atBoundary) {
      setGeneratedBlockAt(x, floorY + lot.height + 2, z, lot.accentId, ck);
    }
    if (!atBoundary && relX === 0 && relZ === 0 && lot.height > 5) {
      setGeneratedBlockAt(x, floorY + lot.height, z, 12, ck);
    }

    return true;
  }

  function maybeGenerateSettlementRoadsideForage(x, z, groundY, profile, localX, localZ, ck) {
    if (Math.abs(localX) <= 2 && Math.abs(localZ) <= 2) return;
    const groundId = getBlockIdAt(x, groundY, z);
    if (groundId !== 1 && groundId !== 2 && groundId !== 26 && groundId !== 49) return;
    const nearRoad = (
      Math.abs(localX) <= profile.roadWidth + 2
      || Math.abs(localZ) <= profile.roadWidth + 2
      || nearestGridDistance(localX, profile.roadStep) <= profile.roadWidth + 2
      || nearestGridDistance(localZ, profile.roadStep) <= profile.roadWidth + 2
    );
    if (!nearRoad) return;

    const patchField = (valueNoise2(x, z, 0.038, 6449) + 1) * 0.5;
    if (patchField <= 0.78) return;
    const clusterField = (valueNoise2(x + 17, z - 9, 0.12, 6457) + 1) * 0.5;
    if (clusterField <= 0.62) return;

    const forageRoll = hash2i(x, z, 6463);
    if (forageRoll <= 0.972) return;

    if (forageRoll > 0.993) {
      setGeneratedBlockAt(x, groundY + 1, z, 68, ck);
      return;
    }
    if (forageRoll > 0.984) {
      setGeneratedBlockAt(x, groundY, z, 49, ck);
      setGeneratedBlockAt(x, groundY + 1, z, 53, ck);
      return;
    }

    const bushId = hash2i(x, z, 6479) > 0.5 ? 66 : 67;
    setGeneratedBlockAt(x, groundY + 1, z, bushId, ck);
  }

  function applySettlementColumn(x, surfaceY, z, ck, generationProfiles = null) {
    const profile = generationProfiles?.settlements
      ? getSquareProfileAtFromCandidates(x, z, generationProfiles.settlements)
      : getSettlementAt(x, z);
    if (!profile) return false;

    const localX = x - profile.centerX;
    const localZ = z - profile.centerZ;
    if (Math.max(Math.abs(localX), Math.abs(localZ)) > profile.radius) return false;

    const blend = profile.style === 'modern' ? 0.9 : profile.style === 'desert' ? 0.84 : 0.78;
    const jitterStrength = profile.style === 'modern' ? 0.6 : 1.1;
    const jitter = Math.round(valueNoise2(x, z, 0.09, 1889) * jitterStrength);
    const targetYRaw = Math.round(lerp(surfaceY, profile.baseY, blend)) + jitter;
    const targetY = Math.floor(clamp(targetYRaw, surfaceY - 4, surfaceY + 5));

    if (isRoadColumn(profile, localX, localZ)) {
      applySettlementRoadColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck);
      return true;
    }

    if (applySettlementBuildingColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck)) {
      return true;
    }

    const groundY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.groundId, profile.fillId);
    clearColumnAbove(x, z, groundY + 1, groundY + 2);
    maybeGenerateSettlementRoadsideForage(x, z, groundY, profile, localX, localZ, ck);
    return true;
  }

  function getDoorSideTowardCenter(cx, cz) {
    if (Math.abs(cx) >= Math.abs(cz)) return cx > 0 ? 'west' : 'east';
    return cz > 0 ? 'north' : 'south';
  }

  function poiTypeConfig(type, humidity = 0.5) {
    const dry = humidity < 0.35;
    if (type === 'hamlet') {
      return {
        blend: 0.72,
        groundId: dry ? 21 : 1,
        fillId: dry ? 21 : 2,
        pathId: dry ? 21 : 6,
        floorId: dry ? 21 : 6,
        wallId: dry ? 21 : 6,
        roofId: dry ? 33 : 34,
        windowId: 23,
        fenceId: 20,
        lampId: 12
      };
    }

    if (type === 'tribe_camp') {
      return {
        blend: 0.68,
        groundId: dry ? 26 : 2,
        fillId: dry ? 26 : 2,
        pathId: dry ? 26 : 6,
        floorId: dry ? 26 : 6,
        wallId: 3,
        roofId: 6,
        windowId: 23,
        fenceId: 20,
        lampId: 12
      };
    }

    if (type === 'sanheyuan') {
      return {
        blend: 0.8,
        groundId: dry ? 21 : 1,
        fillId: dry ? 21 : 2,
        pathId: 77,
        floorId: 77,
        wallId: 6,
        roofId: 22,
        windowId: 23,
        fenceId: 20,
        lampId: 12
      };
    }

    if (type === 'mine_outpost') {
      return {
        blend: 0.82,
        groundId: 74,
        fillId: 75,
        pathId: 77,
        floorId: 29,
        wallId: 74,
        roofId: 75,
        windowId: 23,
        fenceId: 20,
        accentId: 36,
        lampId: 12
      };
    }

    if (type === 'raider_outpost') {
      return {
        blend: 0.82,
        groundId: 28,
        fillId: 28,
        pathId: 25,
        floorId: 28,
        wallId: 29,
        roofId: 34,
        windowId: 23,
        fenceId: 20,
        accentId: 32,
        lampId: 12
      };
    }
    if (type === 'temple') {
      return { blend: 0.82, groundId: 21, fillId: 21, pathId: 21, floorId: 21, wallId: 21, roofId: 21, accentId: 48, windowId: 23, fenceId: 20, lampId: 12 };
    }
    if (type === 'shrine') {
      return { blend: 0.85, groundId: 29, fillId: 5, pathId: 30, floorId: 6, wallId: 16, roofId: 22, accentId: 48, windowId: 23, fenceId: 20, lampId: 12 };
    }
    if (type === 'mansion') {
      return { blend: 0.9, groundId: 3, fillId: 2, pathId: 24, floorId: 6, wallId: 24, roofId: 3, accentId: 23, windowId: 23, fenceId: 20, lampId: 12 };
    }
    if (type === 'castle') {
      return { blend: 0.9, groundId: 28, fillId: 5, pathId: 33, floorId: 30, wallId: 33, roofId: 34, accentId: 36, windowId: 23, fenceId: 20, lampId: 12 };
    }
    if (type === 'colosseum') {
      return { blend: 0.9, groundId: 26, fillId: 26, pathId: 21, floorId: 21, wallId: 21, roofId: 21, accentId: 8, windowId: 23, fenceId: 20, lampId: 12 };
    }
    if (type === 'school') {
      return { blend: 0.8, groundId: 6, fillId: 2, pathId: 24, floorId: 24, wallId: 22, roofId: 22, accentId: 23, windowId: 23, fenceId: 20, lampId: 12 };
    }
    if (type === 'hospital') {
      return { blend: 0.82, groundId: 24, fillId: 2, pathId: 24, floorId: 24, wallId: 24, roofId: 22, accentId: 48, windowId: 23, fenceId: 20, lampId: 12 };
    }

    if (type === 'monster_den') {
      return {
        blend: 0.76,
        groundId: 32,
        fillId: 28,
        pathId: 34,
        floorId: 41,
        wallId: 42,
        roofId: 33,
        coreId: 38,
        fenceId: 20,
        lampId: 12
      };
    }

    if (type === 'aquarium_ruin') {
      return {
        blend: 0.84,
        groundId: 39,
        fillId: 5,
        pathId: 39,
        floorId: 39,
        wallId: 23,
        roofId: 23,
        accentId: 32,
        lampId: 12
      };
    }

    return {
      blend: 0.72,
      groundId: 29,
      fillId: 28,
      pathId: 29,
      floorId: 29,
      wallId: 33,
      roofId: 34,
      accentId: 32,
      lampId: 12
    };
  }

  function createPoiHutSpecs(type, cellX, cellZ, radius) {
    if (type === 'hospital') {
      return [
        {
          cx: 0,
          cz: 0,
          halfW: scaleDistance(4, 3),
          halfD: scaleDistance(3, 2),
          height: scaleHeight(4, 3),
          doorSide: 'south'
        }
      ];
    }

    if (type === 'sanheyuan') {
      const wingHalf = scaleDistance(3, 2);
      const wingDepth = scaleDistance(2, 2);
      const wingHeight = scaleHeight(4, 3);
      const yardOffset = Math.max(3, Math.floor(radius * 0.38));
      return [
        {
          cx: -yardOffset,
          cz: 0,
          halfW: wingHalf,
          halfD: wingDepth + 1,
          height: wingHeight,
          doorSide: 'east'
        },
        {
          cx: yardOffset,
          cz: 0,
          halfW: wingHalf,
          halfD: wingDepth + 1,
          height: wingHeight,
          doorSide: 'west'
        },
        {
          cx: 0,
          cz: -yardOffset,
          halfW: wingHalf + 1,
          halfD: wingDepth,
          height: wingHeight + 1,
          doorSide: 'south'
        },
        {
          cx: 0,
          cz: yardOffset,
          halfW: Math.max(2, wingHalf - 1),
          halfD: Math.max(1, wingDepth - 1),
          height: Math.max(3, wingHeight - 1),
          doorSide: 'north'
        }
      ];
    }

    if (type === 'mine_outpost') {
      return [
        {
          cx: -Math.max(4, Math.floor(radius * 0.35)),
          cz: -Math.max(2, Math.floor(radius * 0.16)),
          halfW: scaleDistance(3, 2),
          halfD: scaleDistance(2, 2),
          height: scaleHeight(4, 3),
          doorSide: 'east'
        },
        {
          cx: Math.max(4, Math.floor(radius * 0.35)),
          cz: Math.max(2, Math.floor(radius * 0.16)),
          halfW: scaleDistance(3, 2),
          halfD: scaleDistance(2, 2),
          height: scaleHeight(4, 3),
          doorSide: 'west'
        }
      ];
    }

    const baseCount = type === 'hamlet' ? 3 : 2;
    const bonus = type === 'hamlet'
      ? Math.floor(hash2i(cellX, cellZ, 5221) * 3)
      : Math.floor(hash2i(cellX, cellZ, 5221) * 2);
    const count = baseCount + bonus;
    const huts = [];

    for (let i = 0; i < count; i++) {
      const angle = ((i / Math.max(1, count)) * Math.PI * 2) + hash2i(cellX + i * 7, cellZ - i * 5, 5231) * 1.08;
      const dist = scaleDistance(Math.max(4, Math.floor(radius * (0.32 + hash2i(cellX + i * 13, cellZ - i * 11, 5239) * 0.3))), 4);
      const cx = Math.round(Math.cos(angle) * dist);
      const cz = Math.round(Math.sin(angle) * dist);
      const halfW = scaleDistance(2 + Math.floor(hash2i(cellX + i * 19, cellZ - i * 17, 5249) * 2), 2);
      const halfD = scaleDistance(2 + Math.floor(hash2i(cellX + i * 23, cellZ - i * 29, 5261) * 2), 2);
      const height = type === 'hamlet'
        ? 3 + Math.floor(hash2i(cellX + i * 31, cellZ - i * 37, 5267) * 3)
        : 3 + Math.floor(hash2i(cellX + i * 41, cellZ - i * 43, 5273) * 2);
      huts.push({
        cx,
        cz,
        halfW,
        halfD,
        height: scaleHeight(height, 3),
        doorSide: getDoorSideTowardCenter(cx, cz)
      });
    }

    return huts;
  }

  function createPoiRuinSegments(cellX, cellZ, radius) {
    const count = 2 + Math.floor(hash2i(cellX, cellZ, 5291) * 3);
    const segments = [];
    for (let i = 0; i < count; i++) {
      const angle = ((i / Math.max(1, count)) * Math.PI * 2) + hash2i(cellX + i * 11, cellZ - i * 7, 5303) * 1.4;
      const dist = scaleDistance(Math.max(3, Math.floor(radius * (0.24 + hash2i(cellX + i * 13, cellZ - i * 17, 5309) * 0.42))), 3);
      segments.push({
        cx: Math.round(Math.cos(angle) * dist),
        cz: Math.round(Math.sin(angle) * dist),
        halfW: scaleDistance(1 + Math.floor(hash2i(cellX + i * 19, cellZ - i * 23, 5323) * 2), 1),
        halfD: scaleDistance(1 + Math.floor(hash2i(cellX + i * 29, cellZ - i * 31, 5333) * 2), 1),
        height: scaleHeight(3 + Math.floor(hash2i(cellX + i * 37, cellZ - i * 41, 5347) * 4), 3),
        decay: 0.32 + hash2i(cellX + i * 43, cellZ - i * 47, 5351) * 0.28,
        seed: 5413 + i * 61
      });
    }
    return segments;
  }

  function getPointOfInterestForCell(cellX, cellZ) {
    const key = `${cellX},${cellZ}`;
    if (poiCache.has(key)) return poiCache.get(key);

    const spawnRoll = hash2i(cellX, cellZ, 5101);
    const centerX = (cellX * POI_CELL_SIZE) + Math.floor(POI_CELL_SIZE * (0.18 + hash2i(cellX, cellZ, 5107) * 0.64));
    const centerZ = (cellZ * POI_CELL_SIZE) + Math.floor(POI_CELL_SIZE * (0.18 + hash2i(cellX, cellZ, 5113) * 0.64));
    const centerInfo = columnNoise(centerX, centerZ);

    const biomeBoost = centerInfo.biome === 'forest'
      ? 0.08
      : centerInfo.biome === 'lowland'
        ? 0.06
        : centerInfo.biome === 'wetland'
          ? 0.09
          : 0.04;
    const humidityBoost = centerInfo.humidity > 0.62 ? 0.05 : (centerInfo.humidity < 0.3 ? 0.04 : 0.0);
    const mountainPenalty = centerInfo.biome === 'mountain' ? 0.07 : 0.0;
    const chance = clamp(POI_BASE_CHANCE + biomeBoost + humidityBoost - mountainPenalty, 0.08, 0.92);

    const aquaticSpawnZone = centerInfo.biome === 'wetland'
      || centerInfo.terrain === 'canyon'
      || centerInfo.terrain === 'ravine';
    if ((centerInfo.height <= WATER_LEVEL + 1 && !aquaticSpawnZone) || spawnRoll > chance) {
      setBoundedCache(poiCache, key, null);
      return null;
    }

    const nearbySettlement = getSettlementAt(centerX, centerZ);
    if (nearbySettlement) {
      const distToSettlement = Math.max(
        Math.abs(centerX - nearbySettlement.centerX),
        Math.abs(centerZ - nearbySettlement.centerZ)
      );
      if (distToSettlement <= nearbySettlement.radius + 10) {
        setBoundedCache(poiCache, key, null);
        return null;
      }
    }

    const typeRoll = hash2i(cellX, cellZ, 5117);
    const nearWaterline = centerInfo.height <= WATER_LEVEL + 4;
    const ruggedWet = (centerInfo.terrain === 'canyon' || centerInfo.terrain === 'ravine') && centerInfo.height <= WATER_LEVEL + 6;
    const aquariumCandidate = aquaticSpawnZone || nearWaterline || ruggedWet;
    let type = 'hamlet';
    if (aquariumCandidate && typeRoll > (centerInfo.biome === 'wetland' ? 0.72 : 0.9)) {
      type = 'aquarium_ruin';
    } else if (centerInfo.biome === 'desert') {
      type = typeRoll < 0.3 ? 'temple' : typeRoll < 0.6 ? 'tribe_camp' : typeRoll < 0.8 ? 'colosseum' : 'ancient_ruin';
    } else if (centerInfo.biome === 'snow') {
      type = typeRoll < 0.4 ? 'castle' : typeRoll < 0.7 ? 'mansion' : 'monster_den';
    } else if (centerInfo.biome === 'wetland') {
      if (typeRoll < 0.28) type = 'hamlet';
      else if (typeRoll < 0.56) type = 'tribe_camp';
      else if (typeRoll < 0.82) type = 'monster_den';
      else type = 'shrine';
    } else if (centerInfo.humidity < 0.28) {
      if (typeRoll < 0.32) type = 'tribe_camp';
      else if (typeRoll < 0.68) type = 'raider_outpost';
      else if (typeRoll < 0.86) type = 'monster_den';
      else type = 'colosseum';
    } else if (centerInfo.terrain === 'volcano') {
      if (typeRoll < 0.42) type = 'mine_outpost';
      else if (typeRoll < 0.7) type = 'raider_outpost';
      else if (typeRoll < 0.88) type = 'castle';
      else type = 'monster_den';
    } else if (centerInfo.biome === 'mountain') {
      if (typeRoll < 0.34) type = 'mine_outpost';
      else if (typeRoll < 0.58) type = 'raider_outpost';
      else if (typeRoll < 0.82) type = 'castle';
      else type = 'mansion';
    } else {
      if (typeRoll < 0.15) type = 'hamlet';
      else if (typeRoll < 0.30) type = 'tribe_camp';
      else if (typeRoll < 0.4) type = centerInfo.terrain === 'hill' ? 'sanheyuan' : 'school';
      else if (typeRoll < 0.52) type = 'hospital';
      else if (typeRoll < 0.65) type = 'shrine';
      else if (typeRoll < 0.78) type = 'mansion';
      else type = 'ancient_ruin';
    }

    const rawRadius = type === 'hamlet'
      ? 13 + Math.floor(hash2i(cellX, cellZ, 5363) * 7)
      : type === 'tribe_camp'
        ? 12 + Math.floor(hash2i(cellX, cellZ, 5371) * 7)
        : (type === 'raider_outpost' || type === 'castle' || type === 'colosseum')
          ? 14 + Math.floor(hash2i(cellX, cellZ, 5381) * 8)
        : type === 'mine_outpost'
            ? 15 + Math.floor(hash2i(cellX, cellZ, 5387) * 8)
        : type === 'monster_den'
            ? 10 + Math.floor(hash2i(cellX, cellZ, 5389) * 5)
            : type === 'aquarium_ruin'
              ? 12 + Math.floor(hash2i(cellX, cellZ, 5393) * 6)
              : type === 'mansion' || type === 'school' || type === 'hospital' || type === 'sanheyuan'
                ? 14 + Math.floor(hash2i(cellX, cellZ, 5397) * 7)
              : 10 + Math.floor(hash2i(cellX, cellZ, 5393) * 6);
    const radius = scaleDistance(rawRadius, 8);

    const typeCfg = poiTypeConfig(type, centerInfo.humidity);
    const aquariumBaseY = Math.min(
      WATER_LEVEL - 1,
      centerInfo.height - 2 + Math.floor(hash2i(cellX, cellZ, 5461) * 2)
    );
    const profile = {
      key,
      cellX,
      cellZ,
      type,
      centerX,
      centerZ,
      radius,
      baseY: type === 'aquarium_ruin'
        ? Math.floor(clamp(aquariumBaseY, WORLD_MIN_Y + 3, WATER_LEVEL + 2))
        : centerInfo.height + (type === 'raider_outpost' ? 1 : 0),
      biome: centerInfo.biome,
      humidity: centerInfo.humidity,
      threat: type === 'monster_den'
        ? 3
        : (type === 'raider_outpost' || type === 'mine_outpost' ? 2 : ((type === 'ancient_ruin' || type === 'aquarium_ruin') ? 1 : 0)),
      faction: type === 'raider_outpost' || type === 'mine_outpost'
        ? 'hostile'
        : (type === 'monster_den' ? 'monsters' : (type === 'aquarium_ruin' ? 'neutral' : 'friendly')),
      ...typeCfg
    };

    const isHutType = ['hamlet', 'tribe_camp', 'school', 'mansion', 'temple', 'shrine', 'hospital', 'sanheyuan', 'mine_outpost'].includes(type);
    if (isHutType) {
      profile.huts = createPoiHutSpecs(type, cellX, cellZ, radius);
      if (!['hospital', 'sanheyuan', 'mine_outpost'].includes(type)) {
        const farmAngle = hash2i(cellX, cellZ, 5407) * Math.PI * 2;
        const farmDist = Math.max(4, Math.floor(radius * (type === 'hamlet' ? 0.42 : 0.36)));
        profile.farm = {
          cx: Math.round(Math.cos(farmAngle) * farmDist),
          cz: Math.round(Math.sin(farmAngle) * farmDist),
          halfW: 2 + Math.floor(hash2i(cellX, cellZ, 5419) * 2),
          halfD: 2 + Math.floor(hash2i(cellX, cellZ, 5431) * 2)
        };
      }
      if (type === 'mine_outpost') {
        profile.mineRailHalf = Math.max(5, Math.floor(radius * 0.86));
      }
    } else if (['raider_outpost', 'castle', 'colosseum'].includes(type)) {
      profile.wallRadius = Math.max(5, radius - 2);
      profile.keepHalf = scaleDistance(2, 2);
      profile.keepHeight = scaleHeight(5 + Math.floor(hash2i(cellX, cellZ, 5441) * 3), 4);
      profile.towerHeight = scaleHeight(profile.keepHeight + 1 + Math.floor(hash2i(cellX, cellZ, 5447) * 2), profile.keepHeight + 1);
      profile.keepTrapDepth = scaleHeight(2 + Math.floor(hash2i(cellX, cellZ, 5453) * 2), 2);
    } else if (type === 'monster_den') {
      profile.ringRadius = Math.max(4, radius - 2);
      profile.coreRadius = scaleDistance(2 + Math.floor(hash2i(cellX, cellZ, 5459) * 2), 2);
      profile.pitDepth = scaleHeight(2 + Math.floor(hash2i(cellX, cellZ, 5471) * 3), 2);
      const denChestDir = Math.floor(hash2i(cellX, cellZ, 5479) * 4);
      const denChestOffset = Math.max(2, Math.floor(profile.ringRadius - 1));
      profile.cacheChestX = denChestDir === 0 ? denChestOffset : denChestDir === 1 ? -denChestOffset : 0;
      profile.cacheChestZ = denChestDir === 2 ? denChestOffset : denChestDir === 3 ? -denChestOffset : 0;
      profile.trapDepth = scaleHeight(2 + Math.floor(hash2i(cellX, cellZ, 5483) * 2), 2);
      profile.trapSeed = 5627 + ((Math.abs(cellX * 17 + cellZ * 29)) % 251);
    } else if (type === 'aquarium_ruin') {
      profile.aquariumRadius = Math.max(5, radius - 2);
      profile.shellHeight = scaleHeight(5 + Math.floor(hash2i(cellX, cellZ, 5487) * 3), 4);
      profile.domeFalloff = scaleHeight(2 + Math.floor(hash2i(cellX, cellZ, 5491) * 3), 2);
      profile.tubeCoreHalf = scaleDistance(1 + Math.floor(hash2i(cellX, cellZ, 5497) * 2), 1);
      profile.aquaChestX = Math.round((2 + Math.floor(hash2i(cellX, cellZ, 5507) * 2)) * (hash2i(cellX, cellZ, 5513) > 0.5 ? 1 : -1));
      profile.aquaChestZ = Math.round((2 + Math.floor(hash2i(cellX, cellZ, 5521) * 2)) * (hash2i(cellX, cellZ, 5527) > 0.5 ? 1 : -1));
    } else {
      profile.ruins = createPoiRuinSegments(cellX, cellZ, radius);
      profile.relicTrapRadius = 2 + Math.floor(hash2i(cellX, cellZ, 5489) * 2);
      profile.relicTrapDepth = 2 + Math.floor(hash2i(cellX, cellZ, 5495) * 2);
      const secretDist = Math.max(3, Math.floor(radius * 0.56));
      const secretAngle = hash2i(cellX, cellZ, 5501) * Math.PI * 2;
      profile.secretChestX = Math.round(Math.cos(secretAngle) * secretDist);
      profile.secretChestZ = Math.round(Math.sin(secretAngle) * secretDist);
    }

    setBoundedCache(poiCache, key, profile);
    return profile;
  }

  function getPointOfInterestProfileAt(x, z) {
    const cellX = Math.floor(x / POI_CELL_SIZE);
    const cellZ = Math.floor(z / POI_CELL_SIZE);

    let best = null;
    let bestDist = Infinity;
    for (let cz = cellZ - 1; cz <= cellZ + 1; cz++) {
      for (let cx = cellX - 1; cx <= cellX + 1; cx++) {
        const profile = getPointOfInterestForCell(cx, cz);
        if (!profile) continue;
        const dx = x - profile.centerX;
        const dz = z - profile.centerZ;
        const dist = Math.max(Math.abs(dx), Math.abs(dz));
        if (dist > profile.radius) continue;
        if (dist < bestDist) {
          bestDist = dist;
          best = profile;
        }
      }
    }
    return best;
  }

  function applyPoiHutColumn(x, z, surfaceY, targetY, profile, localX, localZ, hut, ck) {
    const relX = localX - hut.cx;
    const relZ = localZ - hut.cz;
    if (Math.abs(relX) > hut.halfW || Math.abs(relZ) > hut.halfD) return false;

    const floorY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
    clearColumnAbove(x, z, floorY + 1, floorY + hut.height + 4);

    const atBoundary = Math.abs(relX) === hut.halfW || Math.abs(relZ) === hut.halfD;
    const atCorner = Math.abs(relX) === hut.halfW && Math.abs(relZ) === hut.halfD;
    const doorHere = (
      (hut.doorSide === 'north' && relZ === -hut.halfD && relX === 0) ||
      (hut.doorSide === 'south' && relZ === hut.halfD && relX === 0) ||
      (hut.doorSide === 'west' && relX === -hut.halfW && relZ === 0) ||
      (hut.doorSide === 'east' && relX === hut.halfW && relZ === 0)
    );
    const windowY = Math.max(2, Math.floor(hut.height * 0.55));

    for (let y = 1; y <= hut.height; y++) {
      if (!atBoundary) {
        clearGeneratedBlockAt(x, floorY + y, z);
        continue;
      }

      if (doorHere && y === 1) {
        setGeneratedBlockAt(x, floorY + y, z, 16, ck);
        continue;
      }
      if (doorHere && y === 2) {
        clearGeneratedBlockAt(x, floorY + y, z);
        continue;
      }

      const windowHere = !atCorner && y === windowY && ((Math.abs(relX) + Math.abs(relZ)) % 2 === 0);
      if (windowHere) setGeneratedBlockAt(x, floorY + y, z, profile.windowId, ck);
      else setGeneratedBlockAt(x, floorY + y, z, profile.wallId, ck);
    }

    setGeneratedBlockAt(x, floorY + hut.height + 1, z, profile.roofId, ck);
    if (!atBoundary && relX === 0 && relZ === 0) {
      if (profile.type === 'hospital') {
        setGeneratedBlockAt(x, floorY + 1, z, 14, ck);
        clearColumnAbove(x, z, floorY + 2, floorY + 3);
      } else if (hash2i(x, z, 5483) > 0.55) {
        setGeneratedBlockAt(x, floorY + hut.height, z, profile.lampId, ck);
      }
    }
    return true;
  }

  function applyPoiFarmColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    const farm = profile.farm;
    if (!farm) return false;

    const relX = localX - farm.cx;
    const relZ = localZ - farm.cz;
    if (Math.abs(relX) > farm.halfW || Math.abs(relZ) > farm.halfD) return false;

    const farmY = shapeColumnSurface(x, z, surfaceY, targetY, ck, 49, profile.fillId);
    clearColumnAbove(x, z, farmY + 1, farmY + 3);

    const atEdge = Math.abs(relX) === farm.halfW || Math.abs(relZ) === farm.halfD;
    if (atEdge) {
      if (((Math.abs(relX) + Math.abs(relZ)) % 2 === 0) && hash2i(x, z, 5497) > 0.25) {
        setGeneratedBlockAt(x, farmY + 1, z, profile.fenceId, ck);
      }
      return true;
    }

    if (relX === 0 && relZ === 0) {
      setGeneratedBlockAt(x, farmY, z, 7, ck);
      clearColumnAbove(x, z, farmY + 1, farmY + 2);
      return true;
    }

    const cropId = 50 + Math.floor(hash2i(x, z, 5503) * 4);
    setGeneratedBlockAt(x, farmY + 1, z, cropId, ck);
    return true;
  }

  function applyHamletOrTribeColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    if (profile.type !== 'hospital' && profile.type !== 'mine_outpost' && Math.abs(localX) <= 1 && Math.abs(localZ) <= 1) {
      const plazaY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
      clearColumnAbove(x, z, plazaY + 1, plazaY + 4);
      if (localX === 0 && localZ === 0) {
        if (profile.type === 'tribe_camp') {
          setGeneratedBlockAt(x, plazaY + 1, z, 38, ck);
          setGeneratedBlockAt(x, plazaY + 2, z, profile.lampId, ck);
        } else if (profile.type === 'sanheyuan') {
          setGeneratedBlockAt(x, plazaY, z, 77, ck);
          if (hash2i(x, z, 5511) > 0.52) setGeneratedBlockAt(x, plazaY + 1, z, profile.lampId, ck);
        } else {
          setGeneratedBlockAt(x, plazaY, z, 7, ck);
        }
      }
      return true;
    }

    if (profile.type === 'mine_outpost') {
      const railHalf = Math.max(5, Math.floor(profile.mineRailHalf || profile.radius * 0.86));
      if (Math.abs(localX) <= railHalf && Math.abs(localZ) <= 1) {
        const railY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.pathId, profile.fillId);
        clearColumnAbove(x, z, railY + 1, railY + 4);
        if (Math.abs(localZ) === 1 && hash2i(x, z, 5523) > 0.62) {
          setGeneratedBlockAt(x, railY + 1, z, profile.fenceId, ck);
        } else {
          setGeneratedBlockAt(x, railY + 1, z, 60, ck);
        }

        if (localZ === 0) {
          const nodeStep = Math.max(4, Math.floor(railHalf * 0.46));
          const nodeIndex = Math.floor(Math.abs(localX));
          const cartNode = nodeIndex > 1 && nodeIndex % nodeStep === 1;
          if (cartNode && hash2i(x, z, 5533) > 0.44) {
            setGeneratedBlockAt(x, railY + 2, z, 36, ck);
            if (hash2i(x, z, 5537) > 0.52) setGeneratedBlockAt(x, railY + 3, z, 14, ck);
          }
        }
        return true;
      }
    }

    if (applyPoiFarmColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck)) return true;

    if (Array.isArray(profile.huts)) {
      for (const hut of profile.huts) {
        if (applyPoiHutColumn(x, z, surfaceY, targetY, profile, localX, localZ, hut, ck)) return true;
      }
    }

    if (profile.type === 'sanheyuan') {
      const yardHalf = Math.max(3, Math.floor(profile.radius * 0.25));
      if (Math.abs(localX) <= yardHalf && Math.abs(localZ) <= yardHalf) {
        const yardY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.pathId, profile.fillId);
        clearColumnAbove(x, z, yardY + 1, yardY + 3);
        if (Math.abs(localX) <= 1 && Math.abs(localZ) <= 1 && hash2i(x, z, 5541) > 0.45) {
          setGeneratedBlockAt(x, yardY + 1, z, profile.lampId, ck);
        }
        return true;
      }
    }

    const ringRadius = Math.max(4, profile.radius * 0.52);
    const radialDist = Math.hypot(localX, localZ);
    const ringPath = Math.abs(radialDist - ringRadius) <= 0.9;
    const spokePath = Math.abs(localX) <= 1 || Math.abs(localZ) <= 1;
    if (ringPath || spokePath) {
      const pathY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.pathId, profile.fillId);
      clearColumnAbove(x, z, pathY + 1, pathY + 3);
      if (profile.type === 'tribe_camp' && ringPath && hash2i(x, z, 5519) > 0.84) {
        setGeneratedBlockAt(x, pathY + 1, z, profile.fenceId, ck);
        if (hash2i(x, z, 5531) > 0.62) setGeneratedBlockAt(x, pathY + 2, z, profile.lampId, ck);
      }
      return true;
    }

    const edge = Math.max(Math.abs(localX), Math.abs(localZ));
    if (edge >= profile.radius - 1 && hash2i(x, z, 5539) > 0.72) {
      const edgeY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.groundId, profile.fillId);
      clearColumnAbove(x, z, edgeY + 1, edgeY + 3);
      setGeneratedBlockAt(x, edgeY + 1, z, profile.fenceId, ck);
      return true;
    }

    const groundY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.groundId, profile.fillId);
    clearColumnAbove(x, z, groundY + 1, groundY + 2);
    return true;
  }

  function applyOutpostColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    const wallRadius = profile.wallRadius;
    const corner = wallRadius - 1;
    const towerDx = Math.min(Math.abs(localX - corner), Math.abs(localX + corner));
    const towerDz = Math.min(Math.abs(localZ - corner), Math.abs(localZ + corner));

    if (towerDx <= 1 && towerDz <= 1) {
      const towerY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
      clearColumnAbove(x, z, towerY + 1, towerY + profile.towerHeight + 4);
      const atBoundary = towerDx === 1 || towerDz === 1;
      for (let y = 1; y <= profile.towerHeight; y++) {
        if (atBoundary) setGeneratedBlockAt(x, towerY + y, z, profile.wallId, ck);
        else clearGeneratedBlockAt(x, towerY + y, z);
      }
      setGeneratedBlockAt(x, towerY + profile.towerHeight + 1, z, profile.roofId, ck);
      if (towerDx === 0 && towerDz === 0) setGeneratedBlockAt(x, towerY + profile.towerHeight + 2, z, profile.lampId, ck);
      return true;
    }

    const cheb = Math.max(Math.abs(localX), Math.abs(localZ));
    const onWall = cheb >= wallRadius - 0.5 && cheb <= wallRadius + 0.5;
    if (onWall) {
      const wallY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
      clearColumnAbove(x, z, wallY + 1, wallY + 5);

      const gate = Math.abs(localX) <= 1 && localZ === -wallRadius;
      if (gate) {
        clearColumnAbove(x, z, wallY + 1, wallY + 3);
      } else {
        setGeneratedBlockAt(x, wallY + 1, z, profile.wallId, ck);
        if (hash2i(x, z, 5549) > 0.62) setGeneratedBlockAt(x, wallY + 2, z, profile.accentId, ck);
      }
      return true;
    }

    if (Math.abs(localX) <= profile.keepHalf && Math.abs(localZ) <= profile.keepHalf) {
      const keepY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
      clearColumnAbove(x, z, keepY + 1, keepY + profile.keepHeight + 4);

      const atBoundary = Math.abs(localX) === profile.keepHalf || Math.abs(localZ) === profile.keepHalf;
      const atCorner = Math.abs(localX) === profile.keepHalf && Math.abs(localZ) === profile.keepHalf;
      const doorHere = localX === 0 && localZ === profile.keepHalf;
      for (let y = 1; y <= profile.keepHeight; y++) {
        if (!atBoundary) {
          clearGeneratedBlockAt(x, keepY + y, z);
          continue;
        }
        if (doorHere && y === 1) {
          setGeneratedBlockAt(x, keepY + y, z, 16, ck);
          continue;
        }
        if (doorHere && y === 2) {
          clearGeneratedBlockAt(x, keepY + y, z);
          continue;
        }
        if (!atCorner && y >= 2 && y <= profile.keepHeight - 1 && y % 2 === 0) {
          setGeneratedBlockAt(x, keepY + y, z, profile.windowId, ck);
        } else {
          setGeneratedBlockAt(x, keepY + y, z, profile.wallId, ck);
        }
      }
      setGeneratedBlockAt(x, keepY + profile.keepHeight + 1, z, profile.roofId, ck);
      if (!atBoundary) {
        const trapZ = Math.max(0, profile.keepHalf - 1);
        if (localX === 0 && localZ === trapZ && hash2i(x, z, 5661) > 0.52) {
          const pitDepth = Math.max(2, Math.floor(profile.keepTrapDepth || 2));
          for (let py = keepY; py >= keepY - pitDepth; py--) {
            clearGeneratedBlockAt(x, py, z);
          }
          setGeneratedBlockAt(x, keepY - pitDepth, z, 41, ck);
        }

        const cacheA = localX === -1 && localZ === 0;
        const cacheB = localX === 1 && localZ === -1;
        if ((cacheA || cacheB) && hash2i(x, z, 5671) > (cacheB ? 0.46 : 0.24)) {
          setGeneratedBlockAt(x, keepY + 1, z, 14, ck);
          clearColumnAbove(x, z, keepY + 2, keepY + 3);
        }
      }
      if (localX === 0 && localZ === 0) setGeneratedBlockAt(x, keepY + profile.keepHeight + 2, z, profile.lampId, ck);
      return true;
    }

    if (Math.abs(localX) <= 1 || Math.abs(localZ) <= 1) {
      const roadY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.pathId, profile.fillId);
      clearColumnAbove(x, z, roadY + 1, roadY + 2);
      return true;
    }

    const groundY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.groundId, profile.fillId);
    clearColumnAbove(x, z, groundY + 1, groundY + 2);
    if (hash2i(x, z, 5563) > 0.84 && cheb > 2 && cheb < wallRadius - 1) {
      setGeneratedBlockAt(x, groundY + 1, z, profile.fenceId, ck);
    }
    return true;
  }

  function applyMonsterDenColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    const dist = Math.hypot(localX, localZ);
    const craterFactor = smooth(clamp((profile.coreRadius - dist) / Math.max(0.0001, profile.coreRadius), 0, 1));
    const craterDepth = Math.floor(craterFactor * profile.pitDepth);
    const craterY = targetY - craterDepth;
    const topId = dist <= profile.coreRadius + 0.4 ? profile.floorId : profile.groundId;
    const groundY = shapeColumnSurface(x, z, surfaceY, craterY, ck, topId, profile.fillId);
    clearColumnAbove(x, z, groundY + 1, groundY + 5);

    if (localX === profile.cacheChestX && localZ === profile.cacheChestZ) {
      setGeneratedBlockAt(x, groundY + 1, z, 14, ck);
      clearColumnAbove(x, z, groundY + 2, groundY + 3);
      return true;
    }

    if (dist > profile.coreRadius + 0.9 && dist < profile.ringRadius - 1.1 && hash2i(x, z, profile.trapSeed || 5627) > 0.94) {
      const pitDepth = Math.max(2, Math.floor(profile.trapDepth || 2));
      for (let py = groundY; py >= groundY - pitDepth; py--) {
        clearGeneratedBlockAt(x, py, z);
      }
      setGeneratedBlockAt(x, groundY - pitDepth, z, 41, ck);
      if (hash2i(x, z, 5637) > 0.82) {
        setGeneratedBlockAt(x, groundY - pitDepth + 1, z, 64, ck);
      }
      return true;
    }

    if (dist >= profile.ringRadius - 0.8 && dist <= profile.ringRadius + 0.8) {
      setGeneratedBlockAt(x, groundY + 1, z, profile.wallId, ck);
      if (hash2i(x, z, 5573) > 0.58) setGeneratedBlockAt(x, groundY + 2, z, profile.wallId, ck);
      return true;
    }

    const cardinalPillar = (
      (Math.abs(localX) <= 1 && Math.abs(Math.abs(localZ) - Math.floor(profile.ringRadius - 1)) <= 0) ||
      (Math.abs(localZ) <= 1 && Math.abs(Math.abs(localX) - Math.floor(profile.ringRadius - 1)) <= 0)
    );
    if (cardinalPillar && hash2i(x, z, 5581) > 0.38) {
      setGeneratedBlockAt(x, groundY + 1, z, profile.wallId, ck);
      if (hash2i(x, z, 5591) > 0.65) setGeneratedBlockAt(x, groundY + 2, z, profile.lampId, ck);
      return true;
    }

    if (Math.abs(localX) <= 1 && Math.abs(localZ) <= 1) {
      if (localX === 0 && localZ === 0) {
        setGeneratedBlockAt(x, groundY + 1, z, profile.coreId, ck);
      } else if (hash2i(x, z, 5609) > 0.52) {
        setGeneratedBlockAt(x, groundY + 1, z, profile.wallId, ck);
      }
      return true;
    }

    if (dist < profile.ringRadius - 1.2 && hash2i(x, z, 5623) > 0.86) {
      setGeneratedBlockAt(x, groundY + 1, z, profile.wallId, ck);
    }
    return true;
  }

  function applyAquariumRuinColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    const dist = Math.hypot(localX, localZ);
    if (dist > profile.radius + 0.8) return false;

    const chamberRadius = Math.max(4, Math.floor(profile.aquariumRadius || (profile.radius - 2)));
    const shellHeight = Math.max(4, Math.floor(profile.shellHeight || 5));
    const domeFalloff = Math.max(1, Math.floor(profile.domeFalloff || 2));
    const tubeCoreHalf = Math.max(1, Math.floor(profile.tubeCoreHalf || 1));
    const tubeShellHalf = tubeCoreHalf + 1;

    const baseY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
    const radial = clamp(dist / Math.max(1, chamberRadius), 0, 1);
    const shellTopY = baseY + shellHeight - Math.floor(radial * domeFalloff);
    const shellBottomY = baseY + 1;
    if (shellTopY <= shellBottomY + 1) {
      clearColumnAbove(x, z, shellBottomY + 1, shellBottomY + 3);
      return true;
    }

    clearColumnAbove(x, z, shellBottomY + 1, shellTopY + 3);

    const inOuterShell = dist >= chamberRadius - 1.05 && dist <= chamberRadius + 0.65;
    const inChamber = dist <= chamberRadius - 0.2;
    const inTubeBand = Math.abs(localX) <= tubeShellHalf || Math.abs(localZ) <= tubeShellHalf;
    const inTubeCore = Math.abs(localX) <= tubeCoreHalf || Math.abs(localZ) <= tubeCoreHalf;
    const inTubeShell = inTubeBand && !inTubeCore;

    if (inOuterShell || inTubeShell) {
      for (let y = shellBottomY; y <= shellTopY; y++) {
        setGeneratedBlockAt(x, y, z, profile.wallId, ck);
      }
      return true;
    }

    if (!inChamber) {
      if (dist <= chamberRadius + 0.9 && hash2i(x, z, 5687) > 0.72) {
        setGeneratedBlockAt(x, shellBottomY, z, profile.groundId, ck);
      }
      return true;
    }

    const waterTopY = shellTopY - 1;
    if (inTubeCore) {
      setGeneratedBlockAt(x, shellBottomY, z, profile.pathId, ck);
      for (let y = shellBottomY + 1; y <= waterTopY; y++) {
        clearGeneratedBlockAt(x, y, z);
        convertAdjacentWaterToGlass(x, y, z);
      }
      setGeneratedBlockAt(x, shellTopY, z, profile.roofId, ck);
      convertAdjacentWaterToGlass(x, waterTopY, z);

      if ((localX === profile.aquaChestX && localZ === 0) || (localX === 0 && localZ === profile.aquaChestZ)) {
        setGeneratedBlockAt(x, shellBottomY + 1, z, 14, ck);
        clearColumnAbove(x, z, shellBottomY + 2, shellBottomY + 3);
      } else if (Math.abs(localX) <= 1 && Math.abs(localZ) <= 1 && hash2i(x, z, 5693) > 0.84) {
        setGeneratedBlockAt(x, shellBottomY + 1, z, profile.lampId, ck);
      }
      return true;
    }

    setGeneratedBlockAt(x, shellBottomY, z, profile.floorId, ck);
    for (let y = shellBottomY + 1; y <= waterTopY; y++) {
      setGeneratedBlockAt(x, y, z, 7, ck);
    }
    setGeneratedBlockAt(x, shellTopY, z, profile.roofId, ck);
    return true;
  }

  function applyRuinSegmentColumn(x, z, surfaceY, targetY, profile, localX, localZ, segment, ck) {
    const relX = localX - segment.cx;
    const relZ = localZ - segment.cz;
    if (Math.abs(relX) > segment.halfW || Math.abs(relZ) > segment.halfD) return false;

    const ruinY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
    clearColumnAbove(x, z, ruinY + 1, ruinY + segment.height + 3);

    const atBoundary = Math.abs(relX) === segment.halfW || Math.abs(relZ) === segment.halfD;
    const atCorner = Math.abs(relX) === segment.halfW && Math.abs(relZ) === segment.halfD;
    for (let y = 1; y <= segment.height; y++) {
      if (!atBoundary) {
        clearGeneratedBlockAt(x, ruinY + y, z);
        continue;
      }
      const decay = y > 1 && hash3i(x, ruinY + y, z, segment.seed) < segment.decay;
      if (decay && !atCorner) clearGeneratedBlockAt(x, ruinY + y, z);
      else setGeneratedBlockAt(x, ruinY + y, z, profile.wallId, ck);
    }

    if (atBoundary && hash2i(x, z, segment.seed + 37) > 0.56) {
      setGeneratedBlockAt(x, ruinY + segment.height + 1, z, profile.roofId, ck);
    }
    return true;
  }

  function applyAncientRuinColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck) {
    if (Array.isArray(profile.ruins)) {
      for (const segment of profile.ruins) {
        if (applyRuinSegmentColumn(x, z, surfaceY, targetY, profile, localX, localZ, segment, ck)) return true;
      }
    }

    if (localX === 0 && localZ === 0) {
      const relicY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
      clearColumnAbove(x, z, relicY + 1, relicY + 4);
      setGeneratedBlockAt(x, relicY, z, profile.accentId, ck);
      setGeneratedBlockAt(x, relicY + 1, z, 14, ck);
      if (hash2i(x, z, 5651) > 0.52) setGeneratedBlockAt(x, relicY + 2, z, profile.lampId, ck);
      return true;
    }

    if (localX === profile.secretChestX && localZ === profile.secretChestZ) {
      const cacheY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.floorId, profile.fillId);
      clearColumnAbove(x, z, cacheY + 1, cacheY + 4);
      setGeneratedBlockAt(x, cacheY + 1, z, 14, ck);
      if (hash2i(x, z, 5657) > 0.64) setGeneratedBlockAt(x, cacheY + 2, z, profile.accentId, ck);
      return true;
    }

    const trapRing = Math.max(2, Math.floor(profile.relicTrapRadius || 2));
    if ((Math.abs(localX) + Math.abs(localZ)) === trapRing && hash2i(x, z, 5663) > 0.42) {
      const trapY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.groundId, profile.fillId);
      clearColumnAbove(x, z, trapY + 1, trapY + 3);
      const trapDepth = Math.max(2, Math.floor(profile.relicTrapDepth || 2));
      for (let py = trapY; py >= trapY - trapDepth; py--) {
        clearGeneratedBlockAt(x, py, z);
      }
      setGeneratedBlockAt(x, trapY - trapDepth, z, 41, ck);
      if (hash2i(x, z, 5669) > 0.86) setGeneratedBlockAt(x, trapY - trapDepth + 1, z, 64, ck);
      return true;
    }

    const groundY = shapeColumnSurface(x, z, surfaceY, targetY, ck, profile.groundId, profile.fillId);
    clearColumnAbove(x, z, groundY + 1, groundY + 2);
    if (hash2i(x, z, 5639) > 0.88) {
      setGeneratedBlockAt(x, groundY + 1, z, profile.accentId, ck);
      if (hash2i(x, z, 5647) > 0.66) setGeneratedBlockAt(x, groundY + 2, z, profile.lampId, ck);
    }
    return true;
  }

  function applyPointOfInterestColumn(x, surfaceY, z, ck, generationProfiles = null) {
    const profile = generationProfiles?.pois
      ? getSquareProfileAtFromCandidates(x, z, generationProfiles.pois)
      : getPointOfInterestProfileAt(x, z);
    if (!profile) return false;

    const localX = x - profile.centerX;
    const localZ = z - profile.centerZ;
    if (Math.max(Math.abs(localX), Math.abs(localZ)) > profile.radius) return false;

    const jitterStrength = profile.type === 'raider_outpost' ? 0.75 : 1.05;
    const jitter = Math.round(valueNoise2(x, z, 0.12, 5653) * jitterStrength);
    const targetYRaw = Math.round(lerp(surfaceY, profile.baseY, profile.blend || 0.72)) + jitter;
    const targetY = Math.floor(clamp(targetYRaw, surfaceY - 4, surfaceY + 5));

    if (['hamlet', 'tribe_camp', 'school', 'mansion', 'temple', 'shrine', 'hospital', 'sanheyuan', 'mine_outpost'].includes(profile.type)) {
      return applyHamletOrTribeColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck);
    }
    if (['raider_outpost', 'castle', 'colosseum'].includes(profile.type)) {
      return applyOutpostColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck);
    }
    if (profile.type === 'monster_den') {
      return applyMonsterDenColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck);
    }
    if (profile.type === 'aquarium_ruin') {
      return applyAquariumRuinColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck);
    }
    return applyAncientRuinColumn(x, z, surfaceY, targetY, profile, localX, localZ, ck);
  }

  function getShipwreckForCell(cellX, cellZ) {
    const key = `${cellX},${cellZ}`;
    if (shipwreckCache.has(key)) return shipwreckCache.get(key);

    let centerX = (cellX * SHIPWRECK_CELL_SIZE) + Math.floor(SHIPWRECK_CELL_SIZE * (0.12 + hash2i(cellX, cellZ, 6011) * 0.76));
    let centerZ = (cellZ * SHIPWRECK_CELL_SIZE) + Math.floor(SHIPWRECK_CELL_SIZE * (0.12 + hash2i(cellX, cellZ, 6023) * 0.76));
    let centerInfo = columnNoise(centerX, centerZ);

    let bestScore = Infinity;
    for (let dz = 6; dz <= SHIPWRECK_CELL_SIZE - 6; dz += 8) {
      for (let dx = 6; dx <= SHIPWRECK_CELL_SIZE - 6; dx += 8) {
        const sx = (cellX * SHIPWRECK_CELL_SIZE) + dx;
        const sz = (cellZ * SHIPWRECK_CELL_SIZE) + dz;
        const info = columnNoise(sx, sz);
        const waterDelta = Math.abs(info.height - WATER_LEVEL);
        const wetBias = info.biome === 'wetland' ? -0.5 : 0.5;
        const score = waterDelta + wetBias + Math.abs(info.humidity - 0.55) * 0.8;
        if (score < bestScore) {
          bestScore = score;
          centerX = sx;
          centerZ = sz;
          centerInfo = info;
        }
      }
    }

    const nearWater = centerInfo.height <= WATER_LEVEL + 2 || centerInfo.biome === 'wetland';
    if (!nearWater) {
      setBoundedCache(shipwreckCache, key, null);
      return null;
    }

    const chanceBoost = centerInfo.biome === 'wetland' ? 0.16 : 0.07;
    const depthBoost = centerInfo.height <= WATER_LEVEL ? 0.11 : 0.03;
    const chance = clamp(SHIPWRECK_BASE_CHANCE + chanceBoost + depthBoost, 0.08, 0.82);
    if (hash2i(cellX, cellZ, 6031) > chance) {
      setBoundedCache(shipwreckCache, key, null);
      return null;
    }

    const settlement = getSettlementAt(centerX, centerZ);
    if (settlement) {
      const dist = Math.max(Math.abs(centerX - settlement.centerX), Math.abs(centerZ - settlement.centerZ));
      if (dist <= settlement.radius + 12) {
        setBoundedCache(shipwreckCache, key, null);
        return null;
      }
    }

    const poi = getPointOfInterestProfileAt(centerX, centerZ);
    if (poi) {
      const dist = Math.max(Math.abs(centerX - poi.centerX), Math.abs(centerZ - poi.centerZ));
      if (dist <= poi.radius + 8) {
        setBoundedCache(shipwreckCache, key, null);
        return null;
      }
    }

    const vesselRoll = hash2i(cellX, cellZ, 6037);
    let vesselType = 'shipwreck';
    if (vesselRoll > 0.84 && centerInfo.height <= WATER_LEVEL + 1) vesselType = 'bamboo_raft';
    else if (vesselRoll > 0.52) vesselType = 'sailship';

    const alongX = hash2i(cellX, cellZ, 6043) > 0.5;
    const lengthHalf = vesselType === 'bamboo_raft'
      ? scaleDistance(3 + Math.floor(hash2i(cellX, cellZ, 6059) * 3), 3)
      : scaleDistance(5 + Math.floor(hash2i(cellX, cellZ, 6059) * 4), 4);
    const halfBeam = vesselType === 'bamboo_raft'
      ? scaleDistance(2 + Math.floor(hash2i(cellX, cellZ, 6073) * 2), 2)
      : scaleDistance(2 + Math.floor(hash2i(cellX, cellZ, 6073) * 2), 2);
    const hullDepth = vesselType === 'bamboo_raft'
      ? 1
      : scaleHeight(2 + Math.floor(hash2i(cellX, cellZ, 6089) * 2), 2);
    const mastHeight = vesselType === 'bamboo_raft'
      ? scaleHeight(2 + Math.floor(hash2i(cellX, cellZ, 6097) * 2), 2)
      : scaleHeight(4 + Math.floor(hash2i(cellX, cellZ, 6097) * 3), 3);
    const tilt = (hash2i(cellX, cellZ, 6109) - 0.5) * 2.2;
    const deckY = vesselType === 'bamboo_raft'
      ? Math.round(Math.min(WATER_LEVEL + 1, Math.max(centerInfo.height + 1, WATER_LEVEL)))
      : Math.round(Math.min(WATER_LEVEL + 1, centerInfo.height + (centerInfo.height <= WATER_LEVEL ? 1 : 0)));
    const damage = vesselType === 'shipwreck'
      ? (0.24 + hash2i(cellX, cellZ, 6121) * 0.24)
      : vesselType === 'sailship'
        ? (0.08 + hash2i(cellX, cellZ, 6121) * 0.12)
        : (0.03 + hash2i(cellX, cellZ, 6121) * 0.06);
    const profile = {
      key,
      cellX,
      cellZ,
      type: vesselType,
      centerX,
      centerZ,
      alongX,
      lengthHalf,
      halfBeam,
      hullDepth,
      mastHeight,
      tilt,
      deckY,
      damage,
      chestA: {
        along: Math.max(1, Math.floor(lengthHalf * 0.35)),
        across: (hash2i(cellX, cellZ, 6133) > 0.5 ? 1 : -1) * Math.max(0, halfBeam - 1)
      },
      chestB: {
        along: -Math.max(1, Math.floor(lengthHalf * 0.42)),
        across: 0
      },
      radius: lengthHalf + (vesselType === 'bamboo_raft' ? 2 : 4)
    };

    setBoundedCache(shipwreckCache, key, profile);
    return profile;
  }

  function getShipwreckProfileAt(x, z) {
    const cellX = Math.floor(x / SHIPWRECK_CELL_SIZE);
    const cellZ = Math.floor(z / SHIPWRECK_CELL_SIZE);
    let best = null;
    let bestDist = Infinity;

    for (let cz = cellZ - 1; cz <= cellZ + 1; cz++) {
      for (let cx = cellX - 1; cx <= cellX + 1; cx++) {
        const profile = getShipwreckForCell(cx, cz);
        if (!profile) continue;
        const dx = x - profile.centerX;
        const dz = z - profile.centerZ;
        const along = profile.alongX ? dx : dz;
        const across = profile.alongX ? dz : dx;
        if (Math.abs(along) > profile.lengthHalf + 2 || Math.abs(across) > profile.halfBeam + 3) continue;
        const dist = Math.max(Math.abs(dx), Math.abs(dz));
        if (dist < bestDist) {
          bestDist = dist;
          best = profile;
        }
      }
    }
    return best;
  }

  function applyRaftColumn(x, surfaceY, z, profile, ck) {
    const dx = x - profile.centerX;
    const dz = z - profile.centerZ;
    const along = profile.alongX ? dx : dz;
    const across = profile.alongX ? dz : dx;
    const absAlong = Math.abs(along);
    const absAcross = Math.abs(across);
    if (absAlong > profile.lengthHalf + 1 || absAcross > profile.halfBeam + 1) return false;

    const deckY = Math.min(Math.max(surfaceY, profile.deckY), WATER_LEVEL + 2);
    if (absAlong <= profile.lengthHalf && absAcross <= profile.halfBeam) {
      const edge = absAlong === profile.lengthHalf || absAcross === profile.halfBeam;
      setGeneratedBlockAt(x, deckY, z, edge ? 3 : 6, ck);
      clearColumnAbove(x, z, deckY + 1, deckY + 4);

      if (edge && hash2i(x, z, 6403) > 0.72) {
        setGeneratedBlockAt(x, deckY + 1, z, 20, ck);
      }

      const mastColumn = along === 0 && across === 0;
      if (mastColumn) {
        for (let y = deckY + 1; y <= deckY + profile.mastHeight; y++) {
          setGeneratedBlockAt(x, y, z, 3, ck);
        }
      }

      if (Math.abs(along) <= 1 && absAcross <= Math.max(1, profile.halfBeam - 1)) {
        const sailY = deckY + Math.max(2, profile.mastHeight - 1);
        if (hash2i(x, z, 6413) > 0.2) setGeneratedBlockAt(x, sailY, z, 70, ck);
      }

      const isChest = along === profile.chestA.along && across === profile.chestA.across;
      if (isChest && hash2i(x, z, 6427) > 0.34) {
        setGeneratedBlockAt(x, deckY + 1, z, 14, ck);
      }
      return true;
    }

    if (absAlong <= profile.lengthHalf + 1 && absAcross <= profile.halfBeam + 1 && hash2i(x, z, 6433) > 0.95) {
      const debrisY = Math.min(WATER_LEVEL, deckY);
      setGeneratedBlockAt(x, debrisY, z, hash2i(x, z, 6449) > 0.5 ? 6 : 3, ck);
      return true;
    }

    return false;
  }

  function applyShipwreckColumn(x, surfaceY, z, ck, generationProfiles = null) {
    const profile = generationProfiles?.shipwrecks
      ? getShipwreckAtFromCandidates(x, z, generationProfiles.shipwrecks)
      : getShipwreckProfileAt(x, z);
    if (!profile) return false;

    if (profile.type === 'bamboo_raft') {
      return applyRaftColumn(x, surfaceY, z, profile, ck);
    }

    const dx = x - profile.centerX;
    const dz = z - profile.centerZ;
    const along = profile.alongX ? dx : dz;
    const across = profile.alongX ? dz : dx;
    const absAlong = Math.abs(along);
    const absAcross = Math.abs(across);
    if (absAlong > profile.lengthHalf + 2 || absAcross > profile.halfBeam + 3) return false;

    const tiltOffset = Math.round((along / Math.max(1, profile.lengthHalf)) * profile.tilt);
    const deckY = profile.deckY + tiltOffset;
    const keelY = deckY - profile.hullDepth;

    const insideHull = absAlong <= profile.lengthHalf && absAcross <= profile.halfBeam;
    const atHullEdge = absAlong >= profile.lengthHalf - 1 || absAcross === profile.halfBeam;
    const atBowStern = absAlong >= profile.lengthHalf - 1 && absAcross <= profile.halfBeam;

    if (insideHull) {
      for (let y = keelY; y <= deckY; y++) {
        const isDeck = y === deckY;
        const isKeel = y === keelY;
        const structural = isDeck || isKeel || atHullEdge;
        if (!structural) {
          clearGeneratedBlockAt(x, y, z);
          continue;
        }

        const damaged = !isKeel && !atBowStern && hash3i(x, y, z, 6151) < profile.damage;
        if (damaged) {
          clearGeneratedBlockAt(x, y, z);
          continue;
        }
        const useLog = isKeel || atBowStern;
        const hullId = useLog ? 3 : (profile.type === 'sailship' ? 29 : 6);
        setGeneratedBlockAt(x, y, z, hullId, ck);
      }

      clearColumnAbove(x, z, deckY + 1, deckY + 4);

      if (absAcross === profile.halfBeam && absAlong < profile.lengthHalf - 1 && hash2i(x, z, 6163) > 0.7) {
        setGeneratedBlockAt(x, deckY + 1, z, 20, ck);
      }

      const mastColumn = absAlong <= 1 && across === 0;
      if (mastColumn && hash2i(x, z, 6173) > 0.12) {
        for (let y = deckY + 1; y <= deckY + profile.mastHeight; y++) {
          if (hash3i(x, y, z, 6187) < profile.damage * 0.5 && y > deckY + 2) break;
          setGeneratedBlockAt(x, y, z, 3, ck);
        }
      }

      if (profile.type === 'sailship' && Math.abs(along) <= 1 && absAcross <= profile.halfBeam) {
        const sailBottom = deckY + Math.max(2, Math.floor(profile.mastHeight * 0.35));
        const sailTop = deckY + Math.max(sailBottom, profile.mastHeight - 1);
        for (let y = sailBottom; y <= sailTop; y++) {
          if (hash3i(x, y, z, 6183) < profile.damage * 0.4) continue;
          setGeneratedBlockAt(x, y, z, 70, ck);
        }
      }

      const isChestA = along === profile.chestA.along && across === profile.chestA.across;
      const isChestB = along === profile.chestB.along && across === profile.chestB.across;
      if ((isChestA || isChestB) && hash2i(x, z, 6199) > (isChestB ? (profile.type === 'sailship' ? 0.38 : 0.48) : 0.22)) {
        setGeneratedBlockAt(x, deckY + 1, z, 14, ck);
        clearColumnAbove(x, z, deckY + 2, deckY + 3);
      }

      return true;
    }

    const nearDebrisRing = absAlong <= profile.lengthHalf + 2 && absAcross <= profile.halfBeam + 2;
    if (nearDebrisRing && hash2i(x, z, 6203) > 0.94) {
      const debrisY = Math.min(surfaceY + 1, WATER_LEVEL);
      setGeneratedBlockAt(x, debrisY, z, hash2i(x, z, 6211) > 0.52 ? 6 : 3, ck);
      if (hash2i(x, z, 6221) > 0.78) setGeneratedBlockAt(x, debrisY + 1, z, 18, ck);
      return true;
    }

    return false;
  }

  function getSkyFeatureForCell(cellX, cellZ) {
    const key = `${cellX},${cellZ}`;
    if (skyFeatureCache.has(key)) return skyFeatureCache.get(key);

    const spawnRoll = hash2i(cellX, cellZ, 7101);
    let centerX = (cellX * SKY_FEATURE_CELL_SIZE) + Math.floor(SKY_FEATURE_CELL_SIZE * (0.16 + hash2i(cellX, cellZ, 7117) * 0.68));
    let centerZ = (cellZ * SKY_FEATURE_CELL_SIZE) + Math.floor(SKY_FEATURE_CELL_SIZE * (0.16 + hash2i(cellX, cellZ, 7129) * 0.68));
    let centerInfo = columnNoise(centerX, centerZ);

    let bestScore = -Infinity;
    for (let dz = 8; dz <= SKY_FEATURE_CELL_SIZE - 8; dz += 12) {
      for (let dx = 8; dx <= SKY_FEATURE_CELL_SIZE - 8; dx += 12) {
        const sx = (cellX * SKY_FEATURE_CELL_SIZE) + dx;
        const sz = (cellZ * SKY_FEATURE_CELL_SIZE) + dz;
        const info = columnNoise(sx, sz);
        const terrainBoost = info.terrain === 'mountain'
          ? 1.1
          : info.terrain === 'volcano'
            ? 1.18
          : info.terrain === 'plateau'
            ? 0.9
            : info.terrain === 'hill'
              ? 0.62
            : info.terrain === 'canyon' || info.terrain === 'ravine'
              ? 0.55
              : 0.32;
        const score = info.height * 0.05 + terrainBoost + (0.35 - Math.abs(info.humidity - 0.5));
        if (score > bestScore) {
          bestScore = score;
          centerX = sx;
          centerZ = sz;
          centerInfo = info;
        }
      }
    }

    const terrainBoost = centerInfo.terrain === 'mountain'
      ? 0.09
      : centerInfo.terrain === 'volcano'
        ? 0.12
      : centerInfo.terrain === 'plateau'
        ? 0.06
        : centerInfo.terrain === 'hill'
          ? 0.03
        : centerInfo.terrain === 'canyon' || centerInfo.terrain === 'ravine'
          ? -0.04
          : 0.0;
    const chance = clamp(SKY_FEATURE_BASE_CHANCE + terrainBoost, 0.08, 0.66);
    if (spawnRoll > chance) {
      setBoundedCache(skyFeatureCache, key, null);
      return null;
    }

    const typeRoll = hash2i(cellX, cellZ, 7141);
    const type = typeRoll < 0.4 ? 'ore_rock' : typeRoll < 0.78 ? 'floating_island' : 'sky_city';
    const altitudeBase = 36
      + Math.floor(hash2i(cellX, cellZ, 7153) * 44)
      + Math.floor(centerInfo.height * 0.18)
      + Math.floor((structureSizeScale - 1) * 7);
    const baseY = Math.floor(clamp(altitudeBase, 55, 128));
    const rawRadius = type === 'ore_rock'
      ? 5 + Math.floor(hash2i(cellX, cellZ, 7169) * 5)
      : type === 'floating_island'
        ? 7 + Math.floor(hash2i(cellX, cellZ, 7177) * 7)
        : 10 + Math.floor(hash2i(cellX, cellZ, 7187) * 6);
    const radius = scaleDistance(rawRadius, 4);
    const rawThickness = type === 'ore_rock'
      ? 4 + Math.floor(hash2i(cellX, cellZ, 7193) * 4)
      : type === 'floating_island'
        ? 5 + Math.floor(hash2i(cellX, cellZ, 7201) * 4)
        : 6 + Math.floor(hash2i(cellX, cellZ, 7211) * 4);
    const thickness = scaleHeight(rawThickness, 3);
    const rawTopLift = type === 'ore_rock'
      ? 2 + Math.floor(hash2i(cellX, cellZ, 7219) * 3)
      : type === 'floating_island'
        ? 3 + Math.floor(hash2i(cellX, cellZ, 7223) * 4)
        : 4 + Math.floor(hash2i(cellX, cellZ, 7231) * 4);
    const topLift = scaleHeight(rawTopLift, 2);
    const oreRichness = type === 'ore_rock'
      ? 0.54 + hash2i(cellX, cellZ, 7241) * 0.34
      : 0.1 + hash2i(cellX, cellZ, 7253) * 0.18;
    const treasureChance = type === 'sky_city' ? 0.62 : type === 'ore_rock' ? 0.34 : 0.25;
    const hasTreasure = hash2i(cellX, cellZ, 7261) < treasureChance;

    let streamMode = 'none';
    const streamRoll = hash2i(cellX, cellZ, 7271);
    if (streamRoll > 0.35) {
      if (type === 'sky_city') streamMode = streamRoll > 0.78 ? 'mixed' : 'water';
      else if (centerInfo.terrain === 'volcano') streamMode = streamRoll > 0.44 ? 'lava' : 'mixed';
      else if (centerInfo.terrain === 'canyon' || centerInfo.terrain === 'ravine') streamMode = streamRoll > 0.58 ? 'lava' : 'mixed';
      else streamMode = streamRoll > 0.88 ? 'mixed' : 'water';
    }

    const streamAnchors = [];
    if (streamMode !== 'none') {
      const count = type === 'sky_city' ? 2 : (hash2i(cellX, cellZ, 7283) > 0.72 ? 2 : 1);
      for (let i = 0; i < count; i++) {
        const angle = (hash2i(cellX + i * 7, cellZ - i * 9, 7291) * Math.PI * 2) + i * (Math.PI * 0.45);
        const anchorDist = Math.max(2, Math.floor(radius * (0.45 + hash2i(cellX + i * 13, cellZ - i * 11, 7301) * 0.4)));
        const dx = Math.round(Math.cos(angle) * anchorDist);
        const dz = Math.round(Math.sin(angle) * anchorDist);
        let kind = 'water';
        if (streamMode === 'lava') kind = 'lava';
        else if (streamMode === 'mixed') kind = hash2i(cellX + i * 17, cellZ - i * 19, 7313) > 0.56 ? 'water' : 'lava';
        streamAnchors.push({
          dx,
          dz,
          kind,
          length: scaleDistance(7 + Math.floor(hash2i(cellX + i * 23, cellZ - i * 29, 7321) * 12), 6)
        });
      }
    }

    const towerOffset = scaleDistance(Math.max(3, Math.floor(radius * 0.58)), 3);
    const towerHeightBase = scaleHeight(4 + Math.floor(hash2i(cellX, cellZ, 7331) * 5), 4);
    const towers = type === 'sky_city'
      ? [
        { dx: towerOffset, dz: 0, h: towerHeightBase + 1 },
        { dx: -towerOffset, dz: 0, h: towerHeightBase },
        { dx: 0, dz: towerOffset, h: towerHeightBase + 2 },
        { dx: 0, dz: -towerOffset, h: towerHeightBase + 1 }
      ]
      : [];
    const spiral = type === 'sky_city'
      ? {
        radius: Math.max(2, Math.min(radius - 2, scaleDistance(2 + Math.floor(hash2i(cellX, cellZ, 7339) * 2), 2))),
        pitch: Math.max(3, Math.floor(3 + hash2i(cellX, cellZ, 7343) * 3)),
        rise: scaleHeight(10 + Math.floor(hash2i(cellX, cellZ, 7347) * 9), 9),
        direction: hash2i(cellX, cellZ, 7351) > 0.5 ? 1 : -1
      }
      : null;

    const profile = {
      key,
      type,
      cellX,
      cellZ,
      centerX,
      centerZ,
      baseY,
      radius,
      thickness,
      topLift,
      oreRichness,
      hasTreasure,
      streamAnchors,
      towers,
      spiral,
      cityPlazaRadius: scaleDistance(Math.max(3, Math.floor(radius * 0.42)), 3)
    };
    setBoundedCache(skyFeatureCache, key, profile, 3072);
    return profile;
  }

  function getSkyFeatureAt(x, z) {
    const cellX = Math.floor(x / SKY_FEATURE_CELL_SIZE);
    const cellZ = Math.floor(z / SKY_FEATURE_CELL_SIZE);
    let best = null;
    let bestDist = Infinity;

    for (let cz = cellZ - 1; cz <= cellZ + 1; cz++) {
      for (let cx = cellX - 1; cx <= cellX + 1; cx++) {
        const profile = getSkyFeatureForCell(cx, cz);
        if (!profile) continue;
        const dx = x - profile.centerX;
        const dz = z - profile.centerZ;
        const dist = Math.hypot(dx, dz);
        if (dist > profile.radius + 2.5) continue;
        if (dist < bestDist) {
          bestDist = dist;
          best = profile;
        }
      }
    }
    return best;
  }

  function applySkyFeatureColumn(x, z, ck, generationProfiles = null) {
    const profile = generationProfiles?.skyFeatures
      ? getSkyFeatureAtFromCandidates(x, z, generationProfiles.skyFeatures)
      : getSkyFeatureAt(x, z);
    if (!profile) return false;

    const dx = x - profile.centerX;
    const dz = z - profile.centerZ;
    const dist = Math.hypot(dx, dz);
    if (dist > profile.radius + 2.5) return false;

    const radial = clamp(1 - dist / Math.max(1, profile.radius), 0, 1);
    const capNoise = (valueNoise2(x + profile.cellX * 19, z - profile.cellZ * 23, 0.085, 7349) + 1) * 0.5;
    const topY = Math.round(profile.baseY + radial * profile.topLift + (capNoise - 0.5) * 2.2);
    const thickness = Math.max(2, Math.round(profile.thickness * (0.46 + radial * 0.88)));
    const bottomY = topY - thickness;

    if (radial > 0.03) {
      for (let y = bottomY; y <= topY; y++) {
        if (!canUseNaturalAt(x, y, z)) continue;
        let id = 5;
        if (profile.type === 'ore_rock') {
          const oreNoise = (valueNoise3(x, y, z, 0.17, 7363) + 1) * 0.5;
          if (oreNoise > 0.93 && y <= topY - 1) id = 10;
          else if (oreNoise > (0.85 - profile.oreRichness * 0.16)) id = 47;
          else if (oreNoise > (0.72 - profile.oreRichness * 0.22)) id = 8;
          else if (oreNoise > 0.58) id = 28;
          else id = 5;
        } else if (profile.type === 'floating_island') {
          if (y === topY) id = 1;
          else if (y >= topY - 2) id = 2;
          else id = chooseStoneType(x, y, z);
        } else {
          if (y >= topY - 1) id = 39;
          else if (y >= topY - 3) id = 29;
          else id = chooseStoneType(x, y, z);
        }
        createNaturalBlock(x, y, z, id, ck);
      }
    }

    if (profile.type === 'sky_city' && radial > 0.02) {
      const plazaY = topY + 1;
      const plazaRadius = profile.cityPlazaRadius;
      const onPlaza = dist <= plazaRadius + 0.35;
      const onAxisRoad = (Math.abs(dx) <= 1 && Math.abs(dz) <= profile.radius - 1)
        || (Math.abs(dz) <= 1 && Math.abs(dx) <= profile.radius - 1);
      if (onPlaza || onAxisRoad) {
        setGeneratedBlockAt(x, plazaY, z, onAxisRoad ? 29 : 39, ck);
        if (dist > plazaRadius - 0.9 && dist <= plazaRadius + 0.3 && hash2i(x, z, 7371) > 0.38) {
          setGeneratedBlockAt(x, plazaY + 1, z, 20, ck);
        }
      }

      for (const tower of profile.towers) {
        if (Math.abs(dx - tower.dx) > 1 || Math.abs(dz - tower.dz) > 1) continue;
        const relTx = Math.abs(dx - tower.dx);
        const relTz = Math.abs(dz - tower.dz);
        const atBoundary = relTx === 1 || relTz === 1;
        for (let y = plazaY + 1; y <= plazaY + tower.h; y++) {
          if (atBoundary) setGeneratedBlockAt(x, y, z, 39, ck);
          else clearGeneratedBlockAt(x, y, z);
        }
        setGeneratedBlockAt(x, plazaY + tower.h + 1, z, 33, ck);
        if (relTx === 0 && relTz === 0) setGeneratedBlockAt(x, plazaY + tower.h + 2, z, 12, ck);
      }

      const spiral = profile.spiral;
      if (spiral) {
        const spiralDist = Math.hypot(dx, dz);
        if (spiralDist >= spiral.radius - 0.72 && spiralDist <= spiral.radius + 0.72) {
          const angle = Math.atan2(dz, dx);
          const normalizedAngle = ((angle / (Math.PI * 2)) + 1) % 1;
          const stairStartY = bottomY + 1;
          const stairEndY = Math.min(stairStartY + Math.max(6, spiral.rise), plazaY + Math.max(8, Math.floor(profile.topLift * 2)));
          for (let y = stairStartY; y <= stairEndY; y++) {
            const phaseRaw = (((y - stairStartY) / Math.max(1, spiral.pitch)) * spiral.direction) % 1;
            const phase = phaseRaw < 0 ? phaseRaw + 1 : phaseRaw;
            const delta = Math.abs(phase - normalizedAngle);
            const wrapDelta = Math.min(delta, 1 - delta);
            if (wrapDelta > 0.14) continue;
            setGeneratedBlockAt(x, y, z, 29, ck);
            if (y % 4 === 0 && hash3i(x, y, z, 7399) > 0.6) {
              setGeneratedBlockAt(x, y + 1, z, 20, ck);
            }
          }
        }
      }
    }

    if (profile.hasTreasure && x === profile.centerX && z === profile.centerZ) {
      const chestY = topY + (profile.type === 'sky_city' ? 2 : 1);
      setGeneratedBlockAt(x, chestY, z, 14, ck);
      clearColumnAbove(x, z, chestY + 1, chestY + 2);
    }

    if (Array.isArray(profile.streamAnchors) && profile.streamAnchors.length > 0) {
      for (const stream of profile.streamAnchors) {
        if (x !== profile.centerX + stream.dx || z !== profile.centerZ + stream.dz) continue;
        const sourceY = topY + (profile.type === 'sky_city' ? 2 : 1);
        const fluidId = stream.kind === 'lava' ? 69 : 7;
        const sourcePlaced = createBlock(x, sourceY, z, fluidId, { generated: true, chunkKey: ck, allowReplace: true });
        if (!sourcePlaced) continue;
        if (fluidId === 7) {
          sealFluidWithBarrier(x, sourceY, z, fluidId, ck, { corners: true, floor: true });
        }
        const maxLen = Math.max(4, Math.floor(stream.length || 10));
        for (let i = 1; i <= maxLen; i++) {
          const sy = sourceY - i;
          if (sy <= WATER_LEVEL + 1) break;
          if (!canUseNaturalAt(x, sy, z)) break;
          const placed = createBlock(x, sy, z, fluidId, { generated: true, chunkKey: ck, allowReplace: true });
          if (!placed) break;
          if (fluidId === 7) {
            sealFluidWithBarrier(x, sy, z, fluidId, ck, { corners: false, floor: false });
          }
        }
      }
    }

    return true;
  }


  function getChunkStructureGenerationProfiles(cx, cz) {
    const chunkMinX = cx * CHUNK_SIZE;
    const chunkMinZ = cz * CHUNK_SIZE;
    if (getDimensionAtCoord(chunkMinX, chunkMinZ) !== 'overworld') return null;

    return {
      settlements: collectProfilesForChunk(cx, cz, SETTLEMENT_CELL_SIZE, getSettlementForCell, (profile, bounds) => (
        squareProfileTouchesChunk(profile, bounds)
      )),
      pois: collectProfilesForChunk(cx, cz, POI_CELL_SIZE, getPointOfInterestForCell, (profile, bounds) => (
        squareProfileTouchesChunk(profile, bounds)
      )),
      shipwrecks: collectProfilesForChunk(cx, cz, SHIPWRECK_CELL_SIZE, getShipwreckForCell, shipwreckProfileTouchesChunk),
      skyFeatures: collectProfilesForChunk(cx, cz, SKY_FEATURE_CELL_SIZE, getSkyFeatureForCell, (profile, bounds) => (
        squareProfileTouchesChunk(profile, bounds, 3)
      ))
    };
  }

  function getSettlementSnapshotAt(x, z) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    if (getDimensionAtCoord(gx, gz) !== 'overworld') return null;
    const profile = getSettlementAt(gx, gz);
    if (!profile) return null;
    return {
      type: 'village',
      style: profile.style,
      centerX: profile.centerX,
      centerZ: profile.centerZ,
      radius: profile.radius,
      baseY: profile.baseY
    };
  }

  function getPointOfInterestSnapshotAt(x, z) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    if (getDimensionAtCoord(gx, gz) !== 'overworld') return null;
    const profile = getPointOfInterestProfileAt(gx, gz);
    if (!profile) return null;
    return {
      type: profile.type,
      centerX: profile.centerX,
      centerZ: profile.centerZ,
      radius: profile.radius,
      biome: profile.biome,
      humidity: profile.humidity,
      threat: profile.threat,
      faction: profile.faction
    };
  }

  function getShipwreckSnapshotAt(x, z) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    if (getDimensionAtCoord(gx, gz) !== 'overworld') return null;
    const profile = getShipwreckProfileAt(gx, gz);
    if (!profile) return null;
    return {
      type: profile.type,
      centerX: profile.centerX,
      centerZ: profile.centerZ,
      radius: profile.radius,
      alongX: profile.alongX
    };
  }

  function getSkyFeatureSnapshotAt(x, z) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    if (getDimensionAtCoord(gx, gz) !== 'overworld') return null;
    const profile = getSkyFeatureAt(gx, gz);
    if (!profile) return null;
    return {
      type: profile.type,
      centerX: profile.centerX,
      centerZ: profile.centerZ,
      baseY: profile.baseY,
      radius: profile.radius
    };
  }

  return {
    applySettlementColumn,
    applyPointOfInterestColumn,
    applyShipwreckColumn,
    applySkyFeatureColumn,
    getChunkStructureGenerationProfiles,
    getSettlementSnapshotAt,
    getPointOfInterestSnapshotAt,
    getShipwreckSnapshotAt,
    getSkyFeatureSnapshotAt
  };
}
